import { Button, message, Table, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import TableAction from 'components/TableAction';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import qs from 'qs';
import confirm from 'antd/lib/modal/confirm';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import IconDelete from 'assets/images/IconDelete.svg';
import IconEdit from 'assets/images/IconEdit.svg';
import { deleteKanjis, getListKanjis } from 'requests/kanji';

const levels = [
    { title: 'N5', key: 5 },
    { title: 'N4', key: 4 },
];

export default function KanjiPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const authUser = useSelector((state) => state.auth.authUser);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [currentQuery, setCurrentQuery] = useState({});

    const columns = [
        { title: 'ID', dataIndex: 'id' },
        { title: t('kanji'), dataIndex: 'word' },
        {
            title: t('how_to_read_kanji'),
            dataIndex: 'readingConventions',
            render: (conventions) => {
                const kunConventions = conventions.filter((e) => e.type === 'kun' && !e.learn);
                const onConventions = conventions.filter((e) => e.type === 'on' && !e.learn);
                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            {kunConventions.map((convention, index) => (
                                <div key={index} className="nowrap">
                                    {convention.readingConventionItems.map((e) => e.spellingWord.word).join('')}
                                </div>
                            ))}
                        </div>

                        <div>
                            {onConventions.map((convention, index) => (
                                <div key={index} className="nowrap">
                                    {convention.readingConventionItems.map((e) => e.spellingWord.word).join('')}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            },
        },
        {
            title: t('learn'),
            dataIndex: 'readingConventions',
            render: (conventions) => {
                const learnConventions = conventions.filter((e) => e.learn);
                return (
                    <div>
                        {learnConventions.map((convention, index) => (
                            <div key={index} className="nowrap">
                                {convention.readingConventionItems.map((e) => e.spellingWord.word).join('')}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: t('example_sentence'),
            dataIndex: 'exampleSentence',
        },
        {
            title: t('video_url'),
            dataIndex: 'videoUrl',
            render: (text, { videoStart }) => (
                <a href={videoStart ? text + `?t=${videoStart}` : text} target="_blank" rel="noreferrer">
                    {text}
                </a>
            ),
        },
        {
            title: t('action'),
            render: (text, record, index) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/materials/${record.id}/edit`} type="link">
                        <img src={IconEdit} alt="" />
                    </Link>
                    <Button type="link" onClick={() => handleClickDeleteBtn(record.id)}>
                        <img src={IconDelete} alt="" />
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getData();
    }, [location]);

    const getData = async () => {
        try {
            setLoading(true);

            const queryString = location.search.slice(1);
            const query = qs.parse(queryString);
            setCurrentQuery(query);

            const search = query.search || '';
            const page = query.page || 1;
            const level = query.level || levels[0].key;
            const take = query.pageSize || 10;
            const skip = (page - 1) * take;

            const data = await getListKanjis({ search, level, take, skip });

            if (data.total > 0 && page > Math.ceil(data.total / data.take)) {
                const newQuery = {
                    ...query,
                    page: Math.ceil(data.total / data.take),
                };
                const queryString = qs.stringify(newQuery);

                return navigate({
                    pathname: location.pathname,
                    search: `?${queryString}`,
                });
            }

            setRecords(data.records);
            setPageSize(data.take);
            setTotal(data.total);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    const handleLevelChange = (level) => {
        const query = {
            ...currentQuery,
            level: level,
            page: 1,
            search: '',
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    const handleSearch = (search) => {
        const query = {
            ...currentQuery,
            search,
            page: 1,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    let handleDelete = async () => {
        try {
            setLoading(true);
            await deleteKanjis({ kanjiIds: selectedRowKeys });

            setSelectedRowKeys([]);
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    let handleAdd = () => {
        navigate('/materials/create');
    };

    const handleClickDeleteBtn = (id) => {
        confirm({
            title: t('confirm_delete_one'),
            icon: <ExclamationCircleOutlined />,
            cancelText: t('cancel'),
            onOk() {
                setLoading(true);
                deleteKanjis({
                    kanjiIds: [id],
                })
                    .then(() => getData())
                    .catch(console.log)
                    .finally(() => setLoading(false));
            },
        });
    };

    const handlePageChange = (page, pageSize) => {
        setSelectedRowKeys([]);

        const query = {
            ...currentQuery,
            page: page,
            pageSize: pageSize,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    let rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    const hasSelected = selectedRowKeys.length > 0;
    if (authUser?.role === 2) {
        columns.pop();
        handleAdd = null;
        handleDelete = null;
        rowSelection = null;
    }

    return (
        <>
            <div className="header-panel">
                <Tabs
                    defaultActiveKey={qs.parse(location.search.slice(1)).level || levels[0].key}
                    onChange={handleLevelChange}
                >
                    {levels.map((level) => {
                        return <Tabs.TabPane tab={level.title} key={level.key} />;
                    })}
                </Tabs>
                <TableAction
                    searchPlaceholder={t('search_kanji')}
                    onSearch={handleSearch}
                    onDelete={handleDelete}
                    onAdd={handleAdd}
                    query={currentQuery}
                    disableDeleteBtn={!hasSelected}
                />
            </div>
            <Table
                rowKey="id"
                rowSelection={rowSelection}
                columns={columns}
                dataSource={records}
                loading={loading}
                pagination={{
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    current: Number(currentQuery.page) || 1,
                    onChange: (page, pageSize) => handlePageChange(page, pageSize),
                }}
            />
        </>
    );
}
